import React, { useRef, useState, useEffect, useCallback } from 'react';
import '../leadershipGeneral/leadershipGeneral.css';
import "@fontsource/poppins/400.css";
import "@fontsource/poppins/500.css";
import "@fontsource/poppins/600.css";
import "@fontsource/poppins/700.css";
import PageCover from '../pages/pageCover';
import FloatingActionButtons from '../../floatingActionButtons/floatingActionButtons';
import { downloadPdf, openPdf } from '../utils/pdfGenerator';
import PageTechnicalInfo from '../pages/pageTechnicalInfo';
import { Typography, Switch } from 'antd';
import PageResume from '../pages/pageResume';
import PageCultureResultsByItems from '../pages/pageCultureResultsByItems';
import PageTextualAnswersGeneral from '../pages/pageTextualAnswersGeneral';
import PageCultureObservations from '../pages/pageCultureObservations';

const { Text } = Typography;

const CultureGeneral = ({ totalResults, selectedEvaluation, orgLogo }) => {
    const pdfRef = useRef();
    const [execSummary, setExecSummary] = useState(false);
    let pageNumber = 1;

    useEffect(() => {
    }, [totalResults, selectedEvaluation])

    const pdfFilename = `${selectedEvaluation.evl_title} - Reporte General Cultura.pdf`;
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfProgress, setPdfProgress] = useState(null);
    const onProgress = useCallback((current, total) => setPdfProgress({ current, total }), []);
    const handleDownloadPDF = async () => { setPdfLoading(true); await downloadPdf(pdfRef.current, { filename: pdfFilename, onProgress }); setPdfLoading(false); setPdfProgress(null); };
    const handleOpenPDF = async () => { setPdfLoading(true); await openPdf(pdfRef.current, { filename: pdfFilename, onProgress }); setPdfLoading(false); setPdfProgress(null); };

    // Helpers
    const toArray = (obj = {}) => Object.entries(obj || {}).map(([name, average]) => ({ name, average }));
    const sortDesc = (arr = []) => [...arr].sort((a, b) => b.average - a.average);
    const chunkArray = (array, size) => {
        const result = [];
        for (let i = 0; i < array.length; i += size) result.push(array.slice(i, i + size));
        return result;
    };
    const getDimsArray = (avgByDim = {}) => sortDesc(Object.entries(avgByDim || {}).map(([dim, data]) => ({ name: dim, average: (data?.average || 0) })));
    const mapDictToArray = (dict = {}) => sortDesc(toArray(dict));
    const categoryTitleLabel = (catName = '') => {
        const lc = (catName || '').toLowerCase();
        if (lc.includes('atributo')) return 'Atributo de Cultura';
        if (lc.includes('palanca')) return 'Palanca';
        if (lc.includes('compromiso')) return 'Compromiso';
        return catName;
    };
    const categoryEntityLabels = (catName = '') => {
        const lc = (catName || '').toLowerCase();
        if (lc.includes('atributo')) return { plural: 'Los atributos', singular: 'El atributo' };
        if (lc.includes('palanca')) return { plural: 'Las palancas', singular: 'La palanca' };
        if (lc.includes('compromiso')) return { plural: 'Los ítems de compromiso', singular: 'El ítem de compromiso' };
        return { plural: 'Las dimensiones', singular: 'La dimensión' };
    };

    return (
        <>
            {/* Control Switch */}
            <div className='sticky-switch' >
                <div className='sticky-switch-content'>
                    <Switch
                        checked={execSummary}
                        onChange={setExecSummary}
                        checkedChildren={<span style={{ fontSize: 14 }}>Informe ejecutivo</span>}
                        unCheckedChildren={<span style={{ fontSize: 14 }}>Informe completo</span>}
                    />
                </div>
            </div>

            {/* Contenido PDF */}
            <div ref={pdfRef} style={{ fontFamily: 'Poppins, sans-serif' }} className="pdf-content">
                <PageCover
                    reportTitle={"REPORTE GENERAL"}
                    reportSubtitle={selectedEvaluation.evl_title}
                    orgName={selectedEvaluation.evl_email_responsible}
                    orgRole={selectedEvaluation.evl_email_responsible_job_title}
                    year={new Date(selectedEvaluation.evl_start_date).getFullYear()}
                    general={true}
                    orgLogo={orgLogo}
                />
                <PageTechnicalInfo selectedEvaluation={selectedEvaluation} totalResults={totalResults} individualResults={[]} pageNumber={1} />
                <PageResume selectedEvaluation={selectedEvaluation} totalResults={totalResults} orgLogo={orgLogo} pageNumber={2} isCulture={true} />

                {/* Páginas por categoría: primero dimensiones, luego segmentaciones por dimensión y finalmente subdimensiones */}
                {(totalResults?.average_by_categories || []).map((cat, idxCat) => {
                    const catName = cat?.name || '';
                    const dimsArray = getDimsArray(cat?.average_by_dimension);
                    const dimChunks = chunkArray(dimsArray, 15);

                    return (
                        <React.Fragment key={`cat-${idxCat}`}>
                            {/* Dimensiones por categoría (omitir para Compromiso) */}
                            {(!(`${catName}`.toLowerCase().includes('compromiso')) ? dimChunks : []).map((chunk, j) => {
                                if (chunk.length < 1) return null;
                                pageNumber++;
                                const catLabel = categoryTitleLabel(catName);
                                const catEntities = categoryEntityLabels(catName);
                                return (
                                    <PageCultureResultsByItems
                                        key={`cat-${idxCat}-dims-${j}`}
                                        selectedEvaluation={selectedEvaluation}
                                        average={cat?.average || totalResults?.average}
                                        item={{
                                            title: (<><span>Resultados por </span><span style={{ color: '#3E246B' }}>{catLabel}</span></>),
                                            subtitle: catEntities.plural,
                                            singularSubtitle: catEntities.singular
                                        }}
                                        pageNumber={pageNumber}
                                        chartData={chunk}
                                        isFirstPage={j === 0}
                                        summaryMode="category-dimensions"
                                        categoryName={catName}
                                    />
                                );
                            })}

                            {/* Segmentaciones por dimensión dentro de la categoría */}
                            {Object.entries(cat?.average_by_dimension || {}).map(([dimName, dimData], iDim) => {
                                const allSegs = [
                                    { key: 'average_by_management_area', title: (<><span style={{ color: '#3E246B' }}>{catName}</span>{` — ${dimName} por Gerencia`}</>), subtitle: 'Las gerencias', singular: 'La gerencia', label: 'La gerencia' },
                                    { key: 'average_by_area', title: (<><span style={{ color: '#3E246B' }}>{catName}</span>{` — ${dimName} por Área`}</>), subtitle: 'Las áreas', singular: 'El área', label: 'El área' },
                                    { key: 'average_by_job_category', title: (<><span style={{ color: '#3E246B' }}>{catName}</span>{` — ${dimName} por Categoría de cargo`}</>), subtitle: 'Las categorías de cargo', singular: 'La categoría de cargo', label: 'La categoría de cargo' },
                                    { key: 'average_by_job_title', title: (<><span style={{ color: '#3E246B' }}>{catName}</span>{` — ${dimName} por Cargo`}</>), subtitle: 'Los cargos', singular: 'El cargo', label: 'El cargo' },
                                    { key: 'average_by_headquarter', title: (<><span style={{ color: '#3E246B' }}>{catName}</span>{` — ${dimName} por Sede`}</>), subtitle: 'Las sedes', singular: 'La sede', label: 'La sede' },
                                ];
                                const segs = execSummary ? allSegs.slice(0,1) : allSegs;

                                return (
                                    <React.Fragment key={`cat-${idxCat}-dim-${iDim}`}>
                                        {segs.map((seg, iSeg) => {
                                            const arr = mapDictToArray(dimData?.[seg.key]);
                                            const chunks = chunkArray(arr, 15);
                                            return chunks.map((chunk, j) => {
                                                if (chunk.length < 1) return null;
                                                pageNumber++;
                                                return (
                                                    <PageCultureResultsByItems
                                                        key={`cat-${idxCat}-dim-${iDim}-${seg.key}-${j}`}
                                                        selectedEvaluation={selectedEvaluation}
                                                        average={dimData?.average || cat?.average || totalResults?.average}
                                                        item={{ title: seg.title, subtitle: seg.subtitle, singularSubtitle: seg.singular }}
                                                        pageNumber={pageNumber}
                                                        chartData={chunk}
                                                        isFirstPage={j === 0}
                                                        summaryMode="org-items"
                                                        entityLabel={seg.label}
                                                    />
                                                );
                                            })
                                        })}

                                        {/* Subdimensiones por dimensión: sólo cuando no es resumen ejecutivo */}
                                        {!execSummary && (() => {
                                            const subArr = sortDesc((dimData?.subdimensions || []).map(s => ({ name: s.question, average: s.average })));
                                            const subChunks = chunkArray(subArr, 15);
                                            return subChunks.map((chunk, j) => {
                                                if (chunk.length < 1) return null;
                                                pageNumber++;
                                                return (
                                                    <PageCultureResultsByItems
                                                        key={`cat-${idxCat}-dim-${iDim}-subs-${j}`}
                                                        selectedEvaluation={selectedEvaluation}
                                                        average={dimData?.average || cat?.average || totalResults?.average}
                                                        item={{ title: (<><span style={{ color: '#3E246B' }}>{catName}</span>{` — ${dimName} (Resultados por preguntas)`}</>), subtitle: 'Las preguntas', singularSubtitle: 'La pregunta' }}
                                                        pageNumber={pageNumber}
                                                        chartData={chunk}
                                                        isFirstPage={j === 0}
                                                        summaryMode="subdimensions"
                                                    />
                                                );
                                            });
                                        })()}
                                    </React.Fragment>
                                );
                            })}
                        </React.Fragment>
                    );
                })}

                {/* Páginas de respuestas generales (textuales) */}
                {(() => {
                    const generalBlocks = totalResults?.general || [];
                    const chunkSize = 15; // limitar textos por página (reducido)
                    const pages = [];
                    const isIgnored = (txt) => {
                        if (!txt || typeof txt !== 'string') return true;
                        const s = txt.trim().toLowerCase();
                        if (!s) return true;
                        // normalizar removiendo signos y espacios
                        const norm = s.replace(/\./g, '').replace(/\s+/g, '');
                        const blacklist = new Set(['n/a','na','n.a','noaplica','noaplica','ninguna','nada']);
                        if (blacklist.has(s) || blacklist.has(norm)) return true;
                        // variantes comunes
                        if (s === 'no aplica' || s === 'n / a' || s === 'n- a' || s === 'n a') return true;
                        return false;
                    };
                    generalBlocks.forEach((block) => {
                        const title = block?.title || 'General';
                        (block?.subdimensions || []).forEach((sub) => {
                            if (sub?.type === 'textual') {
                                const question = sub?.question || '';
                                const texts = (sub?.text_answers || []).filter((t) => !isIgnored(t));
                                for (let i = 0; i < texts.length; i += chunkSize) {
                                    const chunk = texts.slice(i, i + chunkSize);
                                    pageNumber++;
                                    pages.push(
                                        <PageTextualAnswersGeneral
                                            key={`gen-${title}-${question}-${i}`}
                                            selectedEvaluation={selectedEvaluation}
                                            title={`Respuestas generales — ${title}`}
                                            question={question}
                                            answers={chunk}
                                            pageNumber={pageNumber}
                                            pagePos={i}
                                        />
                                    );
                                }
                            }
                        });
                    });
                    // Observaciones anidadas por Categoría => Dimensión con chunking y filtrado
                    const observations = totalResults?.observations;
                    if (observations && typeof observations === 'object') {
                        Object.entries(observations).forEach(([cat, dims]) => {
                            Object.entries(dims || {}).forEach(([dim, texts]) => {
                                const filtered = (texts || []).filter((t) => !isIgnored(t));
                                for (let i = 0; i < filtered.length; i += chunkSize) {
                                    const chunk = filtered.slice(i, i + chunkSize);
                                    pageNumber++;
                                    pages.push(
                                        <PageCultureObservations
                                            key={`obs-${cat}-${dim}-${i}`}
                                            selectedEvaluation={selectedEvaluation}
                                            category={cat}
                                            dimension={dim}
                                            texts={chunk}
                                            pageNumber={pageNumber}
                                            pagePos={i}
                                        />
                                    );
                                }
                            });
                        });
                    }
                    return pages;
                })()}
            </div>

            <FloatingActionButtons
                handleDownloadPDF={handleDownloadPDF}
                handleOpenPDF={handleOpenPDF}
                handleExportExcel={() => console.log("Exportando a Excel...")}
                pdfLoading={pdfLoading}
                pdfProgress={pdfProgress}
            />
        </>
    );
};

export default CultureGeneral;
